import { vec3 } from 'gl-matrix';
import { projectPointOntoPlane } from '../slabMembership.js';
import { SHAPE_BOUNDARY_EPSILON, createColumnLineResolver, createPlaneBasis, toIntegerRun, } from './shapeGeometry.js';
export function createPolylineShape(options) {
    const { volume, viewPlaneNormal: normal, polyline } = options;
    if (!polyline?.length) {
        throw new Error('A polyline shape needs an outline');
    }
    const inputRings = Array.isArray(polyline[0]?.[0])
        ? polyline
        : [polyline];
    if (inputRings.some((ring) => !ring?.length || ring.length < 3)) {
        throw new Error('Every polyline ring needs at least three points');
    }
    const planePoint = options.planePoint ?? inputRings[0][0];
    const candidate = Math.abs(normal[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
    const basis = createPlaneBasis(normal, vec3.cross(vec3.create(), normal, candidate));
    const toPlanePoint = (point) => [
        (point[0] - planePoint[0]) * basis.u[0] +
            (point[1] - planePoint[1]) * basis.u[1] +
            (point[2] - planePoint[2]) * basis.u[2],
        (point[0] - planePoint[0]) * basis.v[0] +
            (point[1] - planePoint[1]) * basis.v[1] +
            (point[2] - planePoint[2]) * basis.v[2],
    ];
    const toPlaneDirection = (vector) => [
        vector[0] * basis.u[0] + vector[1] * basis.u[1] + vector[2] * basis.u[2],
        vector[0] * basis.v[0] + vector[1] * basis.v[1] + vector[2] * basis.v[2],
    ];
    const rings = inputRings.map((ring) => ring.map((point) => toPlanePoint(projectPointOntoPlane(point, planePoint, basis.n))));
    let extent = 0;
    for (const ring of rings) {
        for (const [x, y] of ring) {
            extent = Math.max(extent, Math.abs(x), Math.abs(y));
        }
    }
    const boundarySlack = Math.max(extent, 1) * SHAPE_BOUNDARY_EPSILON;
    function distanceToOutline(planePoint2) {
        let best = Infinity;
        for (const ring of rings) {
            const distance = distanceToRing(ring, planePoint2);
            if (distance < best) {
                best = distance;
            }
        }
        return best;
    }
    function distanceToRing(ring, [x, y]) {
        let best = Infinity;
        const vertexCount = ring.length;
        for (let i = 0, j = vertexCount - 1; i < vertexCount; j = i++) {
            const [xi, yi] = ring[i];
            const [xj, yj] = ring[j];
            const edgeX = xj - xi;
            const edgeY = yj - yi;
            const lengthSquared = edgeX * edgeX + edgeY * edgeY;
            let t = 0;
            if (lengthSquared > 0) {
                t = ((x - xi) * edgeX + (y - yi) * edgeY) / lengthSquared;
                t = t < 0 ? 0 : t > 1 ? 1 : t;
            }
            const dx = x - (xi + t * edgeX);
            const dy = y - (yi + t * edgeY);
            const distance = Math.hypot(dx, dy);
            if (distance < best) {
                best = distance;
            }
        }
        return best;
    }
    const resolveColumnLine = createColumnLineResolver(volume, basis.n);
    function containsPlanePoint([x, y]) {
        let inside = false;
        for (const ring of rings) {
            const vertexCount = ring.length;
            for (let i = 0, j = vertexCount - 1; i < vertexCount; j = i++) {
                const [xi, yi] = ring[i];
                const [xj, yj] = ring[j];
                if (yi > y !== yj > y) {
                    const crossingX = xi + ((y - yi) / (yj - yi)) * (xj - xi);
                    if (x < crossingX) {
                        inside = !inside;
                    }
                }
            }
        }
        return inside || distanceToOutline([x, y]) <= boundarySlack;
    }
    function containsPoint(point) {
        return containsPlanePoint(toPlanePoint(projectPointOntoPlane(point, planePoint, basis.n)));
    }
    function* getRuns(outerIndex, rowIndex, depthRun, slab) {
        const line = resolveColumnLine(slab, outerIndex, rowIndex);
        const origin2 = toPlanePoint(line.projectedBase);
        const direction2 = toPlaneDirection(line.projectedStep);
        const directionLengthSquared = direction2[0] * direction2[0] + direction2[1] * direction2[1];
        if (directionLengthSquared <= 0) {
            if (containsPlanePoint(origin2)) {
                yield [depthRun[0], depthRun[1]];
            }
            return;
        }
        const directionLength = Math.sqrt(directionLengthSquared);
        const slack = boundarySlack / directionLength;
        const perpendicularScale = 1 / directionLength;
        const columnOf = ([x, y]) => ((x - origin2[0]) * direction2[0] + (y - origin2[1]) * direction2[1]) /
            directionLengthSquared;
        const sideOf = ([x, y]) => direction2[0] * (y - origin2[1]) - direction2[1] * (x - origin2[0]);
        const intervals = [];
        const crossings = [];
        for (const ring of rings) {
            const vertexCount = ring.length;
            let previousIndex = vertexCount - 1;
            let previousSide = sideOf(ring[previousIndex]);
            for (let i = 0; i < vertexCount; i++) {
                const side = sideOf(ring[i]);
                const previousOnLine = Math.abs(previousSide) * perpendicularScale <= boundarySlack;
                const currentOnLine = Math.abs(side) * perpendicularScale <= boundarySlack;
                if (previousOnLine && currentOnLine) {
                    const a = columnOf(ring[previousIndex]);
                    const b = columnOf(ring[i]);
                    intervals.push(a <= b ? [a - slack, b + slack] : [b - slack, a + slack]);
                }
                else if (currentOnLine) {
                    const at = columnOf(ring[i]);
                    intervals.push([at - slack, at + slack]);
                }
                if (previousSide > 0 !== side > 0) {
                    const t = previousSide / (previousSide - side);
                    const crossingX = ring[previousIndex][0] + t * (ring[i][0] - ring[previousIndex][0]);
                    const crossingY = ring[previousIndex][1] + t * (ring[i][1] - ring[previousIndex][1]);
                    crossings.push(columnOf([crossingX, crossingY]));
                }
                previousIndex = i;
                previousSide = side;
            }
        }
        crossings.sort((a, b) => a - b);
        for (let pair = 0; pair + 1 < crossings.length; pair += 2) {
            intervals.push([crossings[pair] - slack, crossings[pair + 1] + slack]);
        }
        if (!intervals.length) {
            return;
        }
        intervals.sort((a, b) => a[0] - b[0]);
        let [low, high] = intervals[0];
        for (let index = 1; index < intervals.length; index++) {
            const [nextLow, nextHigh] = intervals[index];
            if (nextLow <= high) {
                high = Math.max(high, nextHigh);
                continue;
            }
            const run = toIntegerRun([low, high]);
            if (run) {
                yield run;
            }
            low = nextLow;
            high = nextHigh;
        }
        const finalRun = toIntegerRun([low, high]);
        if (finalRun) {
            yield finalRun;
        }
    }
    return {
        containsPoint,
        getRuns,
        getRequiredThickness: () => 0,
    };
}
